import React, { useContext, useState } from "react";
import {
  Typography,
  TextField,
  IconButton,
  Box,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { useForm, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { GlobalDataContext } from "../../contexts/GlobalData";
import { LanguageOutlined, Settings } from "@mui/icons-material";
import Wrapper from "../wrappers/Wrapper";
import MultiLangForm from "./MultiLangForm";
import UserHint from "../display/UserHint";
import useDelete from "../../hooks/useDelete";
import usePut from "../../hooks/usePut";
import { overflowingTitleSx } from "../../enums/Styles";

export default function MultiLangAccordion({
  object,
  titleKey = "title",
  refetch,
  queryKeyEdit,
  queryKeyDel,
  editPath,
  deletePath,
  deleteConfirmationMessage,
  permissions = { edit: true, delete: true },
  deletePermission = "delete_permission",
  editPermission = "edit_permission",
  maxLength = 255,
}) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const { t, i18n } = useTranslation();
  const { isPermissionAllowed } = useContext(GlobalDataContext);
  const currentLanguage = i18n.language;
  const translatedTitle =
    object?.translations?.[currentLanguage] || object?.[titleKey];

  const put = usePut(editPath, queryKeyEdit);

  const { ConfirmationDialog: DeletingConfirmation, deleteFn } = useDelete(
    object?.id,
    deletePath,
    null,
    deleteConfirmationMessage,
    null,
    queryKeyDel,
  );

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      [titleKey]: object?.[titleKey],
    },
  });

  const handleFlip = () => {
    setIsFlipped((prev) => !prev);
    setIsAccordionExpanded(false);
  };

  const handleAccordionToggle = () => {
    setIsAccordionExpanded((prev) => !prev);
  };

  const editSubmit = async (data) => {
    const updatedObject = {
      id: object?.id,
      [titleKey]: data[titleKey],
    };

    try {
      await put.submitCallback(updatedObject);
      setIsFlipped(false);
    } catch (error) {}
  };

  const handleDelete = async () => {
    await deleteFn();
  };

  return (
    <Box mb={2}>
      {isFlipped ? (
        // BACK SIDE
        <form onSubmit={handleSubmit(editSubmit)}>
          <Wrapper>
            <Controller
              name={titleKey}
              control={control}
              rules={{ required: t("CantBeEmpty") }}
              render={({ field }) => (
                <TextField
                  {...field}
                  key={object?.id}
                  size="small"
                  label={t("Fallback")}
                  fullWidth
                  error={!!errors[titleKey]}
                  helperText={errors[titleKey] ? errors[titleKey].message : ""}
                  inputProps={{ maxLength: maxLength }}
                />
              )}
            />

            <Stack direction={"horizontal"} spacing={2}>
              <IconButton
                onClick={handleFlip}
                color="error"
                title={t("Cancel")}
              >
                <CloseIcon />
              </IconButton>

              <IconButton
                type="submit"
                disabled={
                  !permissions.edit || !isPermissionAllowed(editPermission)
                }
                color="info"
                title={t("Save")}
              >
                <CheckIcon />
              </IconButton>
            </Stack>
          </Wrapper>

          <UserHint hint={"ServiceTitleEditInformation"} />
        </form>
      ) : (
        // FRONT SIDE
        <Accordion
          expanded={isAccordionExpanded && editPermission}
          onChange={handleAccordionToggle}
        >
          <AccordionSummary
            expandIcon={
              <IconButton disabled={!editPermission} title={t("Settings")}>
                <Settings />
              </IconButton>
            }
          >
            <Wrapper noPaper>
              <Stack direction={"row"} spacing={1}>
                <Typography sx={overflowingTitleSx}>
                  {translatedTitle}
                </Typography>
                <Typography color={"gray"}>
                  {`(${t("Fallback")}: ${object?.[titleKey]})`}
                </Typography>
              </Stack>

              <Stack direction={"horizontal"} spacing={2}>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete();
                  }}
                  disabled={
                    !permissions?.delete ||
                    !isPermissionAllowed(deletePermission)
                  }
                  color="error"
                  title={t("Delete")}
                >
                  <DeleteIcon />
                </IconButton>

                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFlip();
                  }}
                  disabled={
                    !permissions?.edit || !isPermissionAllowed(editPermission)
                  }
                  title={t("Edit")}
                >
                  <EditIcon />
                </IconButton>
              </Stack>
            </Wrapper>
          </AccordionSummary>

          <AccordionDetails>
            <Chip icon={<LanguageOutlined />} label={t("Translation")} />
            <Divider sx={{ mt: 2 }} />

            <MultiLangForm
              btnMsg={"Save"}
              object={object}
              refetch={refetch}
              mutationKey={queryKeyEdit}
              postUrl={editPath}
            />
          </AccordionDetails>
        </Accordion>
      )}

      <DeletingConfirmation />
    </Box>
  );
}
